import type { ReactNode } from 'react';

import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';

import { FieldRow } from './FieldRow';

export interface MultiToggleOption {
  value: string;
  label: string;
}

export interface MultiToggleFieldProps {
  label: ReactNode;
  description?: ReactNode;
  value: string[];
  options: MultiToggleOption[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
}

/**
 * Fixed-option multi-select rendered as a checkbox group. Used for enum arrays
 * (delivery models, payment methods, notification channels). Preserves the
 * option order on toggle for a stable payload.
 */
export function MultiToggleField({
  label,
  description,
  value,
  options,
  onChange,
  disabled = false,
}: MultiToggleFieldProps) {
  const items = value ?? [];
  const toggle = (optionValue: string, checked: boolean) => {
    if (checked) {
      const next = options.filter((o) => items.includes(o.value) || o.value === optionValue);
      onChange(next.map((o) => o.value));
    } else {
      onChange(items.filter((v) => v !== optionValue));
    }
  };

  return (
    <FieldRow label={label} description={description} alignTop>
      <FormGroup>
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            control={
              <Checkbox
                size="small"
                checked={items.includes(option.value)}
                disabled={disabled}
                onChange={(event) => toggle(option.value, event.target.checked)}
              />
            }
            label={option.label}
          />
        ))}
      </FormGroup>
    </FieldRow>
  );
}
